import "./quote-board-header.css";

import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import { RadioGroup } from "@tql/ui-components-react/kendo/inputs";
import type { TabStripSelectEventArguments } from "@tql/ui-components-react/kendo/layout";
import { TabStrip, TabStripTab } from "@tql/ui-components-react/kendo/layout";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { DropDownList } from "@tql/ui-components-react/tql/dropdowns";
import {
	filterLinesLine,
	refreshCcw_03,
	searchSmLine,
} from "@tql/ui-components-react/untitled/icons";
import { useEffect, useState } from "react";
import type { z } from "zod";

import { ColumnChooser } from "~/shared/components/column-chooser/column-chooser.tsx";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useFeatureFlagGate } from "~/shared/hooks/use-feature-flag-gate.ts";
import { useSharedUserFilter } from "~/shared/hooks/use-shared-user-filter.ts";
import {
	customerSchema,
	laeSchema,
	ltlOwnerSchema,
} from "~/shared/schemas/customer-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import type {
	BoardColumn,
	QuoteBoardChip,
} from "~/shared/types/quote-board-types.ts";

import { QUOTE_TYPE_OPTIONS } from "../constants/quote-type-constants.ts";
import { timeFrames } from "../constants/time-frame-constants.ts";
import { QuoteBoardGlobalFilters } from "./quote-board-global-filters.tsx";
import { QuoteIdSearch } from "./quote-id-search.tsx";

type QuoteBoardHeaderProps = {
	chips: QuoteBoardChip[];
	onSetChips: (newChip: string) => void;
	onRefresh: () => void;
	onSetTimeFrame: ({ value }: { value: number }) => void;
	onSetQuoteId: (quoteId: number | null) => void;
	columns: BoardColumn[];
	onChangeColumn: (column: BoardColumn) => void;
	onResetColumns?: () => void;
	quoteType: string;
	onQuoteTypeChange: (value: string) => void;
	/** Mobile renders cards instead of the grid, so column visibility is moot. */
	isMobile?: boolean;
};

type UserFilter = {
	id: number;
	name: string;
	endpoint: string;
	schema: z.ZodSchema<unknown> | null;
	paramType: string;
};

const userFilters: UserFilter[] = [
	{
		id: 1,
		name: "LTL AR",
		endpoint: apiRoutes.GETLTLBROKERS,
		schema: ltlOwnerSchema?.array(),
		paramType: "ltlBrokers",
	},
	{
		id: 2,
		name: "LAE",
		endpoint: apiRoutes.GETPRIMARYBROKERS,
		schema: laeSchema?.array(),
		paramType: "laes",
	},
	{
		id: 3,
		name: "Customer",
		endpoint: apiRoutes.CUSTOMERSEARCH,
		schema: customerSchema?.array(),
		paramType: "customers",
	},
];

export const QuoteBoardHeader = ({
	chips,
	onSetChips,
	onRefresh,
	onSetTimeFrame,
	columns,
	onChangeColumn,
	onSetQuoteId,
	onResetColumns,
	quoteType,
	onQuoteTypeChange,
	isMobile = false,
}: QuoteBoardHeaderProps) => {
	const { isInternal } = useDeploymentType();

	const { isReady: isBulkUploadReady, isEnabled: isBulkUploadEnabled } =
		useFeatureFlagGate("traxLtlBulkUpload");
	const {
		isReady: isExternalTimeFilterReady,
		isEnabled: isExternalTimeFilterEnabled,
	} = useFeatureFlagGate("traxLtlExternalTimeFilter");

	useEffect(() => {
		if (!isBulkUploadEnabled && quoteType !== "Individual") {
			onQuoteTypeChange("Individual");
		}
	}, [isBulkUploadEnabled, quoteType, onQuoteTypeChange]);

	const [searchValue, setSearchValue] = useState<number | null>(null);

	const {
		selectedFilter: selectedFilterFromHook,
		value: selectedValue,
		handleChange,
		handleSelectFilter,
	} = useSharedUserFilter(userFilters);

	const selectedFilter = selectedFilterFromHook ?? userFilters[0];

	const [selectedTab, setSelectedTab] = useState<number>(0);

	const handleSelectTab = (e: TabStripSelectEventArguments) => {
		setSelectedTab(e.selected);
		// Clear the Quote ID search when switching tabs so the user filter (held
		// in the shared atom) drives the request again. The atom itself is
		// preserved across tab switches and page navigations.
		onSetQuoteId(null);
	};

	const externalTimeFilter =
		isExternalTimeFilterReady && isExternalTimeFilterEnabled ? (
			<div className={"quote-board-user-filter-group"}>
				<div className={"quote-board-user-filter-group-value"}>
					<div>Time Frame</div>
					<div data-testid={"quote-board-time-frame"}>
						<DropDownList
							className={"time-frame-dropdown"}
							data={timeFrames}
							dataItemKey={"value"}
							defaultValue={timeFrames[0]}
							onChange={(e: { value: { name: string; value: number } }) =>
								onSetTimeFrame(e.value)
							}
							textField={"name"}
						/>
					</div>
				</div>
			</div>
		) : null;

	return (
		<div
			aria-label={"Quote board actions"}
			className={
				isMobile
					? "quote-board-header quote-board-header-fixed quote-board-header-mobile"
					: "quote-board-header quote-board-header-fixed"
			}
			data-testid={"quote-board-actions"}
			role={"toolbar"}
		>
			<div>
				{isInternal ? (
					<TabStrip
						className={"tabstrip-no-border"}
						onSelect={handleSelectTab}
						selected={selectedTab}
					>
						<TabStripTab
							title={
								<div className={"quote-board-tab-title"}>
									<SvgIcon icon={filterLinesLine} /> Filter
								</div>
							}
						>
							<QuoteBoardGlobalFilters
								onHandleChange={handleChange}
								onHandleSelectFilter={handleSelectFilter}
								onQuoteTypeChange={onQuoteTypeChange}
								onSetTimeFrame={onSetTimeFrame}
								quoteType={quoteType}
								selectedFilter={selectedFilter}
								selectedValue={selectedValue}
								userFilters={userFilters}
							/>
						</TabStripTab>

						<TabStripTab
							title={
								<div className={"quote-board-tab-title"}>
									<SvgIcon icon={searchSmLine} /> Search by Quote ID
								</div>
							}
						>
							<QuoteIdSearch
								onSetQuoteId={onSetQuoteId}
								searchValue={searchValue}
								setSearchValue={setSearchValue}
							/>
						</TabStripTab>
					</TabStrip>
				) : (
					externalTimeFilter
				)}
			</div>

			{isBulkUploadReady && isBulkUploadEnabled ? (
				<RadioGroup
					className={"quote-type-selection"}
					data={QUOTE_TYPE_OPTIONS}
					layout={"horizontal"}
					onChange={(e: { value: string }) => onQuoteTypeChange(e.value)}
					value={quoteType}
				/>
			) : null}

			<div className={"quote-board-header-group"}>
				<Button
					aria-label={"Refresh"}
					onClick={onRefresh}
					svgIcon={refreshCcw_03}
					title={"Refresh"}
					variant={"tertiary"}
				></Button>
				{isMobile ? null : (
					<ColumnChooser
						secondary
						columns={columns}
						onColumnToggle={onChangeColumn}
						onResetColumns={onResetColumns}
					/>
				)}
			</div>

			{isMobile ? (
				<div className={"quote-board-mobile-search"}>
					<div className={"quote-board-mobile-search-input-wrapper"}>
						<input
							aria-label={"Search quotes"}
							className={"quote-board-mobile-search-input"}
							data-testid={"quote-board-mobile-search-input"}
							onChange={(event) => onSetChips(event.target.value)}
							placeholder={"Search"}
							value={chips[0]?.value ?? ""}
						/>
						<SvgIcon
							className={"quote-board-mobile-search-icon"}
							icon={searchSmLine}
						/>
					</div>
				</div>
			) : null}
		</div>
	);
};
